import {ChangeDetectionStrategy, Component, inject, Input} from '@angular/core';
import {SelectOptions} from '@aviary/store/common/common.types';
import {MatButton, MatFabButton, MatIconButton} from '@angular/material/button';
import {ActionButtonsComponent} from '@aviary/components/action-buttons/action-buttons.component';
import {RadioGroupFormFieldComponent} from '@aviary/forms/fields/radio-group-form-field/radio-group-form-field.component';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {ExtendedFormBuilder} from '@aviary/forms/extensions/extended-form-builder/extended-form-builder';
import {SplitButtonComponent, SplitButtonDefinition} from '@aviary/buttons/split-button/split-button.component';
import {getExtendedControlFrom} from '@aviary/forms/form.helpers';
import {MatIcon} from '@angular/material/icon';
import {isPopulatedArray} from '@aviary/store/common/typing.helpers';
import {FilledButtonComponent} from '@aviary/buttons/filled-button/filled-button.component';
import {OutlinedButtonComponent} from '@aviary/buttons/outlined-button/outlined-button.component';
import {TextButtonComponent} from '@aviary/buttons/text-button/text-button.component';
import {ElevatedButtonComponent} from '@aviary/buttons/elevated-button/elevated-button.component';
import {IconButtonComponent} from '@aviary/buttons/icon-button/icon-button.component';
import {FabComponent} from '@aviary/buttons/fab/fab.component';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {LinkButtonComponent} from '@aviary/buttons/link-button/link-button.component';

const DEFAULT_BUTTON_COLOR: string = 'primary';
const DEFAULT_BACKGROUND_COLOR: string = 'surface';

const ICON_POSITIONS = ['none', 'start', 'end', 'both'] as const;
type IconPosition = typeof ICON_POSITIONS[number];
const DEFAULT_ICON_POSITION: IconPosition = 'none';

type ExtraContentOption = 'none' | 'custom' | 'small-label' | 'status-dot';
const DEFAULT_EXTRA_CONTENT_OPTION: ExtraContentOption = 'none';

const LABEL_AFFIX_OPTIONS = ['none', 'before', 'after', 'both'] as const;
type LabelAffixOption = typeof LABEL_AFFIX_OPTIONS[number];
const DEFAULT_LABEL_AFFIX_OPTION: LabelAffixOption = 'none';

type TooltipOption = 'none' | 'same-for-both' | 'disabled-only' | 'different-for-each';
const DEFAULT_TOOLTIP_OPTION: TooltipOption = 'none';

export type ButtonType = 'filled' | 'outlined' | 'text' | 'elevated' | 'split' | 'icon' | 'fab';
export type SupportedButtonTypesByColor = Record<string, ButtonType[]>;

@Component({
  selector: 'mx-component-lib-buttons',
  host: {
    class: 'component-lib-content'
  },
  templateUrl: './component-lib-buttons.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ActionButtonsComponent, MatButton, RadioGroupFormFieldComponent, SplitButtonComponent, MatIcon, MatIconButton, MatFabButton, FilledButtonComponent,
    OutlinedButtonComponent, TextButtonComponent, ElevatedButtonComponent, IconButtonComponent, FabComponent, LinkButtonComponent
  ]
})
export class ComponentLibButtonsComponent {

  @Input({required: true}) public set supportedButtonTypesByColor(supportedButtonTypesByColor: SupportedButtonTypesByColor) {
    this._supportedButtonTypesByColor = supportedButtonTypesByColor;

    const colors: string[] = Object.keys(supportedButtonTypesByColor);

    this.colorOptions = colors.map(color => ({displayName: color, value: color}));
    this.hasMultipleColorOptions = colors.length > 1;
    this.buttonColorControl.setValue(colors[0]);
  }

  @Input() public set backgroundColors(backgroundColors: string[]) {
    if (!isPopulatedArray(backgroundColors)) return;

    this.backgroundColorOptions = backgroundColors.map(color => ({displayName: color, value: `bg-${color}`}));
    this.hasMultipleBackgroundColorOptions = backgroundColors.length > 1;
    this.backgroundColorControl.setValue(this.backgroundColorOptions[0].value);
  }

  @Input() public useAviaryButtonComponents: boolean = false;

  public allButtonsEnabled: boolean = true;
  public linkButtonClickCount: number = 0;

  public colorOptions: SelectOptions<string> = [{displayName: DEFAULT_BUTTON_COLOR, value: DEFAULT_BUTTON_COLOR}];
  public hasMultipleColorOptions: boolean = false;

  public backgroundColorOptions: SelectOptions<string> = [{displayName: DEFAULT_BACKGROUND_COLOR, value: `bg-${DEFAULT_BACKGROUND_COLOR}`}];
  public hasMultipleBackgroundColorOptions: boolean = false;

  public buttonTypeOptions: SelectOptions<boolean> = [{displayName: 'Supported button types', value: false}, {displayName: 'All button types', value: true}];

  public iconPositionOptions: SelectOptions<IconPosition> = ICON_POSITIONS.map(position => ({
    displayName: `${position.charAt(0).toUpperCase()}${position.slice(1)}`,
    value: position
  }));
  public tooltipOptions: SelectOptions<TooltipOption> = [
    {displayName: 'None', value: 'none'},
    {displayName: 'Same for enabled/disabled', value: 'same-for-both'},
    {displayName: 'Disabled only', value: 'disabled-only'},
    {displayName: 'Different for enabled/disabled', value: 'different-for-each'}
  ];
  public extraContentOptions: SelectOptions<ExtraContentOption> = [
    {displayName: 'None', value: 'none'},
    {displayName: 'Custom', value: 'custom'},
    {displayName: 'Small label', value: 'small-label'},
    {displayName: 'Status dot', value: 'status-dot'}
  ];
  public labelAffixOptions: SelectOptions<LabelAffixOption> = LABEL_AFFIX_OPTIONS.map(option => ({
    displayName: `${option.charAt(0).toUpperCase()}${option.slice(1)}`,
    value: option
  }));

  public readonly formGroup: ExtendedFormGroup = inject(ExtendedFormBuilder).nonNullable.extendedGroup({
    buttonColor: [DEFAULT_BUTTON_COLOR],
    backgroundColor: [`bg-${DEFAULT_BACKGROUND_COLOR}`],
    iconPosition: [DEFAULT_ICON_POSITION],
    showButtonTypes: [false],
    tooltips: [DEFAULT_TOOLTIP_OPTION],
    extraContent: [DEFAULT_EXTRA_CONTENT_OPTION],
    labelAffixes: [DEFAULT_LABEL_AFFIX_OPTION]
  });

  public readonly splitButtonDefinition: SplitButtonDefinition = {
    mainAction: {
      label: 'Split'
    },
    additionalActions: [
      {
        label: 'Additional Action',
      },
      {
        label: 'Another Additional Action',
      }
    ]
  };

  public get selectedColor(): string {
    return this.buttonColorControl.value;
  }

  public get selectedBackgroundColor(): string {
    return this.backgroundColorControl.value;
  }

  public get selectedIconPosition(): IconPosition {
    return this.iconPositionControl.value;
  }

  public get showAllButtonTypes(): boolean {
    return this.showButtonTypesControl.value;
  }

  public get selectedTooltips(): TooltipOption {
    return this.tooltipsControl.value;
  }

  public get selectedExtraContent(): ExtraContentOption {
    return this.extraContentControl.value;
  }

  public get selectedLabelAffixes(): LabelAffixOption {
    return this.labelAffixesControl.value;
  }

  private _supportedButtonTypesByColor: SupportedButtonTypesByColor;
  private readonly buttonColorControl: ExtendedFormControl = getExtendedControlFrom(this.formGroup, 'buttonColor');
  private readonly backgroundColorControl: ExtendedFormControl = getExtendedControlFrom(this.formGroup, 'backgroundColor');
  private readonly iconPositionControl: ExtendedFormControl = getExtendedControlFrom(this.formGroup, 'iconPosition');
  private readonly showButtonTypesControl: ExtendedFormControl = getExtendedControlFrom(this.formGroup, 'showButtonTypes');
  private readonly tooltipsControl: ExtendedFormControl = getExtendedControlFrom(this.formGroup, 'tooltips');
  private readonly extraContentControl: ExtendedFormControl = getExtendedControlFrom(this.formGroup, 'extraContent');
  private readonly labelAffixesControl: ExtendedFormControl = getExtendedControlFrom(this.formGroup, 'labelAffixes');

  public supportsButtonType(type: ButtonType): boolean {
    return this.showAllButtonTypes || (this._supportedButtonTypesByColor[this.selectedColor] ?? []).includes(type);
  }

  public onClickLinkButton(): void {
    this.linkButtonClickCount++;
  }
}
